// What the upload wizard's Next button is still waiting for, as sentences.
//
// These are the rules the `isDisabled` computed property already applied --
// same four steps, same fields -- with one deliberate tightening on the
// location step, argued where it is made. What is new otherwise is that the
// answer can be READ OUT rather than only felt as a button that will not go.
//
// A disabled button carries no explanation of its own: it is removed from the
// tab order, so a screen reader user never lands on it to hear a tooltip, and
// there is nothing for a `title` or an `aria-describedby` to be announced
// from. The wizard's only feedback was a red line under three of the seven
// fields, and none at all for the missing photo on step 1. WCAG 3.3.1 asks
// that an error be identified in text; a control that refuses to act and says
// nothing identifies nothing.
//
// Pure on purpose, like wizard-route.js: vitest in this repo does not compile
// .vue files (vitest.config.mjs), so a rule left inside the component is a rule
// with no test -- and this one decides whether anybody can submit at all.

/**
 * Everything still missing on `step`, in the order the fields appear on it.
 *
 * The order matters: the list is rendered next to the button, and it reads
 * top-to-bottom against the fields it is talking about, so a visitor can walk
 * up the form without hunting.
 *
 * Only the current step is answered for. The wizard cannot reach step 4 with
 * step 2 unfinished, and an error naming a field that is not on screen is an
 * error nobody can act on where they are standing.
 *
 * @param {number} step 1-based wizard step, matching the component's `step`.
 * @param {object} state
 * @param {string} state.fileName Name of the chosen photo, '' when none.
 * @param {Array} state.categories `art.categories`.
 * @param {Array} state.tags `art.tags`.
 * @param {string} state.title `art.title`.
 * @param {Array} state.artists `art.artists`.
 * @param {boolean} state.hasCoordinates Whether a real lat/lng has been fixed --
 *   from the photo's EXIF, the browser's position, a Places selection, or the
 *   visitor moving the pin.
 * @returns {string[]} Empty when the step is satisfied.
 */
export function missingRequirements(step, state) {
  const {
    fileName,
    categories,
    tags,
    title,
    artists,
    hasCoordinates,
  } = state || {}

  if (step === 1) {
    // `fileName` and not the base64 preview: the preview is produced
    // asynchronously by FileReader and is still empty for a second or two on a
    // large photo that is perfectly valid, so gating on it would flicker the
    // button back to disabled after the visitor had already chosen.
    return hasText(fileName) ? [] : ['A photo of the artwork']
  }

  if (step === 2) {
    const missing = []
    if (!hasEntries(categories)) missing.push('At least one category')
    if (!hasEntries(tags)) missing.push('At least one descriptive tag')
    // Trimmed: `art.title` is posted verbatim, and a piece called " " renders
    // as a blank link on the map and cannot be searched for.
    if (!hasText(title)) missing.push('A title for the artwork')
    if (!hasEntries(artists)) missing.push('At least one artist')
    return missing
  }

  if (step === 4) {
    // Coordinates and nothing else, because coordinates are the only thing the
    // map and the Art model actually consume. They can arrive with no address
    // at all -- from the photo's EXIF, from a Places selection, or from the
    // visitor dropping the pin somewhere the geocoder has no street name for --
    // so demanding text on top of a placed pin would block a submission that is
    // already complete.
    //
    // Address text alone is deliberately NOT enough, and that is a change. The
    // old rule accepted `art.address` on its own, which was safe only by
    // accident: the Address field's v-model was inert (PlacesAutocomplete had
    // no `value` prop and emitted no `input`), so the only thing that could
    // ever write `art.address` was a Places selection, and a Places selection
    // always carries a location. Repairing that binding makes typing reach
    // `art.address` for the first time -- and `art.lat`/`art.lng` start at 0,
    // while `validates :lat, presence: true` (app/models/art.rb:6) is satisfied
    // by 0. Accepting text alone would therefore file the piece at 0,0, a buoy
    // in the Gulf of Guinea, with the server raising nothing.
    if (hasCoordinates) return []
    return ['A location: pick an address suggestion, or move the pin on the map']
  }

  // Step 3 (city, year, website, description) is entirely optional today, and
  // an unrecognised step falls open for a harder reason: the gate is a DISABLED
  // button. Guessing "something is missing" for a step we do not know about
  // leaves a wizard nobody can finish and no message that explains why, while
  // guessing "nothing is missing" costs at worst one API call that the Rails
  // model validates anyway.
  return []
}

function hasEntries(value) {
  return Array.isArray(value) && value.length > 0
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0
}

export default missingRequirements
